import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { v3 } from "oc-threejs/short3";
import * as Maths3 from "oc-threejs/Maths3";
import * as Commons from "oc-commons/Commons";
import { isEmpty } from "oc-commons/Commons";
import * as Maths from "oc-commons/Maths";
import { getMMR } from "../RichMetadata";
import _ from "lodash";
import { heightBoxWorldPhysical } from "../commons/Utils";
import { PivotKette } from "oc-jaco3d/arrangement/PivotKette";
import * as Scenes from "oc-threejs/Scenes";
import { Box3, FrontSide, Group, Matrix4, Vector3 } from "three";
import * as Textures from "oc-threejs/Textures";
import { Images } from "oc-canvas2d/Images";
import { ProfilHighlighter } from "./ProfilHighlighter";
import type { MObjekt } from "../../metamodell";
import { Füllung3d } from "../arrangement3d/Füllung3d";
import { ghostPool } from "./Arrangement";
import { ModelGhost } from "oc-jaco3d/arrangement/ModelGhost";

const epsilon = 0.0001;
const cutNowhere = Symbol();
const cutTop = Symbol();
const cutBottom = Symbol();

/**
 * @param elementMeta any
 * @param sizeY number
 * @returns boolean
 */
export const sollAbschlussleisteObenErscheinen = (elementMeta: any, sizeY: number): boolean => {
  return (
    elementMeta.MaximalhöheAbschlussleisteOben < epsilon ||
    sizeY < elementMeta.MaximalhöheAbschlussleisteOben + epsilon
  );
};

export class Füllung extends Jaco {
  static CLASS_NAME = "Füllung";

  // ----------------------------------------------------------------------------------
  // abgeleitete Werte - d.h. sind letztlich nur berechnet (in update3d) und gecached, kein Zustand
  // Achtung, diese Werte muss man immer ganz besonders im Auge haben, kann sein,
  // dass sie fälschlich doch als Zustand dienen, und das führt dann zu schwierigen Bugs
  // ----------------------------------------------------------------------------------

  Aufbautyp: string | null = null;
  meta: MObjekt | null = null;
  modelle: ModelGhost[] = [];
  modelleImmerStücklistenRelevant: ModelGhost[] = [];
  size: Vector3 = v3();
  box: Box3 = new Box3();
  shuffleMaterialIndexOffset: number = Math.round(Math.random() * 1000);
  pivotPoint: Jaco = new Jaco(this);
  drehbaresObjekt: PivotKette;
  senkrechtprofilDrehachsen: PivotKette[] = [];
  profilHighlighter: ProfilHighlighter | null = null;
  physicalHeight: number;
  detailBildGroup: Group;

  constructor(
    parent: Jaco,
    public flügel: any,
    public element: any
  ) {
    super(parent);
    this.drehbaresObjekt = new PivotKette(this);
  }

  traverse(iterator: (j: Jaco) => void): void {
    super.traverse(iterator);
    if (this.pivotPoint) this.pivotPoint.traverse(iterator);
    if (this.drehbaresObjekt) this.drehbaresObjekt.traverse(iterator);
    this.senkrechtprofilDrehachsen.forEach((o) => o.traverse(iterator));
    this.modelle.forEach((o) => o.traverse(iterator));
    this.profilHighlighter?.traverse(iterator);
  }

  getBox(): Box3 {
    return this.box.clone();
  }

  update3d(): void {
    super.update3d();
    Maths3.boxFromGridMinAndSize(v3(0, 0, 0.5), this.size, this.box);
    this.size.z = 0;
    this.modelle = ghostPool(this).clearInstances(this.modelle);
    this.modelleImmerStücklistenRelevant = [];
    if (
      this.Aufbautyp == "Platten" ||
      this.Aufbautyp == "Kein_Zuschnitt_System" ||
      this.Aufbautyp == "Kein_Zuschnitt_Holzzaun" ||
      this.Aufbautyp == "Zuschnitt_Longlife"
    ) {
      this.updatePlatten();
    } else if (this.Aufbautyp == "Profile") {
      this.updateProfile();
    } else if (this.Aufbautyp == "SystemSenkrechtprofile") {
      this.updateSystemSenkrechtprofile();
    } else if (this.Aufbautyp == "SenkrechtprofileFix") {
      this.updateSenkrechtprofileFix();
    } else if (this.Aufbautyp == "SenkrechtprofileVariabel") {
      this.updateSenkrechtprofileVariabel();
    } else if (this.Aufbautyp == "VersetzteSenkrechtprofileVariabel") {
      this.updateVersetzteSenkrechtprofileVariabel();
    }
    this.updatePivotPoint();
    this.drehbaresObjekt.update3d();

    this.modelle.forEach((m) => {
      m.update3d();
    });

    this.updateDetailBild();
    this.updateProfilHighlighter();
  }

  updateProfilHighlighter(): void {
    const dekorTeilMeta = this.element.teilInfo(this.element.profilMeta)?.modelMaterialRelation;
    const dekorprofile = this.modelle.filter((m) => m.getMeta() == dekorTeilMeta);
    if (dekorprofile.length > 0) {
      this.profilHighlighter = new ProfilHighlighter(this.pivotPoint, this.element, dekorprofile);
    } else {
      this.profilHighlighter = null;
    }
    this.profilHighlighter?.update3d();
  }

  updatePivotPoint(): void {
    const isHolzzaun = this.Aufbautyp == "Kein_Zuschnitt_Holzzaun";
    const isAufbauProfile = this.Aufbautyp == "Profile";
    const {
      isSenkrechtprofile,
      anschlussAufRechts,
      isTor,
      isAufhängungAnLinkerTorseite,
      seiteGewechselt,
      isAnschluss,
      isMultipleFüllungsseiten,
      isÖffnungNachInnen,
      isFüllungsseiteInvertiert,
    } = this.element;
    const scaleX = anschlussAufRechts && !isHolzzaun && !isAufbauProfile ? -1 : 1;
    const elementYRotation = (): number => {
      if (isTor && isHolzzaun) {
        return isFüllungsseiteInvertiert != !seiteGewechselt ? Math.PI : 0;
      } else if (isHolzzaun && isAnschluss) {
        // Bei Holzzaun-Anschlüssen ist die Schräge fest im Modell modelliert und wird
        // ausschließlich über diese Y-Drehung gespiegelt. Welche Seite hoch ist, hängt
        // – genau wie die Pfostenhöhen (siehe pfostenFunctions: istLinks ==
        // anschlussAufRechts) – allein an anschlussAufRechts, nicht an seiteGewechselt.
        // Zuvor zog die seiteGewechselt-Basisdrehung das Modell mit, sodass das
        // Umschalten von "Anschlusselemente umdrehen" die Schräge nicht (bzw. in die
        // falsche Richtung) drehte und der hohe Pfosten an der niedrigen Modellseite stand.
        return anschlussAufRechts ? Math.PI : 0;
      } else {
        return (isFüllungsseiteInvertiert != seiteGewechselt &&
          !(isAnschluss && isMultipleFüllungsseiten && !isAufbauProfile)) ||
          (isHolzzaun && anschlussAufRechts)
          ? Math.PI
          : 0;
      }
    };
    const rotateY = elementYRotation();
    const rotateZ = isSenkrechtprofile ? -Math.PI / 2 : 0;
    const p = v3(
      (isSenkrechtprofile ? this.size.y : this.size.x) / 2,
      isSenkrechtprofile ? this.size.y / 2 : 0,
      0
    );
    const translate1 = new Matrix4().makeTranslation(-p.x, -p.y, -p.z);
    const translate0 = new Matrix4().makeTranslation(p.x, p.y, p.z);
    const scale = new Matrix4().makeScale(scaleX, 1, 1);
    const rotationY = new Matrix4().makeRotationY(rotateY);
    const rotationZ = new Matrix4().makeRotationZ(rotateZ);
    const pivotMatrix = new Matrix4()
      .multiply(translate0)
      .multiply(scale)
      .multiply(rotationZ)
      .multiply(rotationY)
      .multiply(translate1);
    this.pivotPoint.copyMatrix(pivotMatrix);
    this.pivotPoint.update3d();
  }

  addModel(
    meta: any,
    size: Vector3,
    fixGridPosition: Vector3,
    additionalCuttingOptions: any = {},
    parent: Jaco = this.pivotPoint
  ): ModelGhost {
    const mirrorMeta = (): any => meta.ModelMaterialRelation[0]?.Mirrored?.Objekt ?? meta;
    const mmr = getMMR(this.flügel.mightNeedMirroring ? mirrorMeta() : meta);
    const modelSize = APP.modelsHelper.getBoundingBox(mmr).getSize(v3());
    this.size.z = Math.max(modelSize.z, this.size.z);

    const model = ghostPool(this).getForModelMaterialRelation(parent, mmr, {
      size,
      fixGridPosition,
      gehrung45: false,
      ...additionalCuttingOptions,
    });
    (model as any).stücklistenMeta = meta;
    this.modelle.push(model);
    return model;
  }

  getAnschlussDiagHeight(): number {
    return this.element.anschlussDiagHeight;
  }

  getAufsatzleisteOffset(): number {
    return this.element.aufsatzleisteOffset;
  }

  updatePlatten(): void {
    const { maybeAddLeisteUnten, maybeAddLeisteOben, addTeil, leiste, profil, getY, hasRahmen } =
      this.teilBuilderY();
    maybeAddLeisteUnten();
    const leistenHöhe =
      getY() +
      (leiste.oben
        ? leiste.oben.modelSize.y + leiste.oben.modelMaterialRelation.Model.Profilabstand
        : 0);
    const useRahmenInnenabstand = hasRahmen && this.element.isAros;
    const rahmenInnenBox = this.flügel.rahmenInnenBox.clone();
    const rahmenInnenBreite = rahmenInnenBox.getSize(v3()).x;
    const füllungsBreite = useRahmenInnenabstand ? rahmenInnenBreite : this.size.x;
    addTeil(
      profil,
      this.size.y - leistenHöhe,
      cutTop,
      false,
      {},
      false,
      false,
      useRahmenInnenabstand ? füllungsBreite : undefined,
      useRahmenInnenabstand ? rahmenInnenBox.min.x : 0
    );
    const isAnschluss = this.element.meta.Mass.Form.Key == "Anschluss";
    const isKeinZuschnittHolzzaun = this.Aufbautyp == "Kein_Zuschnitt_Holzzaun";
    maybeAddLeisteOben(
      isAnschluss && isKeinZuschnittHolzzaun
        ? -1 * Math.atan(füllungsBreite / this.getAnschlussDiagHeight())
        : undefined
    );
    this.physicalHeight = getY();
  }

  updateProfile(isSenkrechtprofile = false): void {
    const {
      maybeAddLeisteUnten,
      maybeAddLeisteOben,
      addTeil,
      leiste,
      passtTeil,
      profil,
      dekorprofil,
      hasRahmen,
      getY,
      getAnzahlTeile,
      modelHeight,
      profilabstand,
    } = this.teilBuilderY(isSenkrechtprofile);
    const sizeX = isSenkrechtprofile ? this.size.y : this.size.x;
    const sizeY = isSenkrechtprofile ? this.size.x : this.size.y;
    !isSenkrechtprofile && maybeAddLeisteUnten();
    const wegschneidenHöheUnterstesProfilData: { offsetY: number; profilPositionDelta: number } =
      this.element.getWegschneidenHöheUnterstesProfil();
    let wegschneidenHöheUnterstesProfilOffsetY = wegschneidenHöheUnterstesProfilData.offsetY;
    const isAnschluss = this.element.meta.Mass.Form.Key == "Anschluss";
    const profilHöhe = modelHeight(profil);
    const anzahlProfile = Math.floor(sizeY / (profilHöhe + profilabstand(profil)));
    const gesamtanzahlZugeschnittenerProfile = anzahlProfile / 2;
    const anschlussLeft = this.element.seiteGewechselt != this.element.anschlussAufRechts;
    let profilPosition = this.element.sonderprofilPosition;
    if (profilPosition !== "center") {
      profilPosition -= wegschneidenHöheUnterstesProfilData.profilPositionDelta;
    }

    // Dieser Block zentriert senkrechte Profile, so dass auf beiden Seiten gleich abgeschnitten wird
    if (isSenkrechtprofile) {
      const profilabstandPositionsunabhängig = (p: any) =>
        p.modelMaterialRelation.Model.Profilabstand;
      const profilAbstand = profilabstandPositionsunabhängig(profil);

      let platzZuFüllen = sizeY;
      if (dekorprofil) {
        const reducePlatz = (einTeil: any) => {
          if (einTeil) {
            const dekorprofilHöhe = modelHeight(einTeil);
            const dekorprofilAbstand = profilabstandPositionsunabhängig(einTeil);
            platzZuFüllen -= dekorprofilHöhe + dekorprofilAbstand;
          }
        };
        reducePlatz(dekorprofil);
        reducePlatz(leiste.dekorprofilleisteUnten);
        reducePlatz(leiste.dekorprofilleisteOben);
      }
      let anzahlNormaleProfile = Math.ceil(platzZuFüllen / (profilHöhe + profilAbstand));
      if (dekorprofil && profilPosition == "center") {
        anzahlNormaleProfile = 2 * Math.ceil(platzZuFüllen / 2 / (profilHöhe + profilAbstand));
      }
      const platzNormaleProfile =
        anzahlNormaleProfile * profilHöhe + (anzahlNormaleProfile - 1) * profilAbstand;
      const rest = platzNormaleProfile - platzZuFüllen;
      wegschneidenHöheUnterstesProfilOffsetY = rest / 2 + profilAbstand;
      if (profilPosition == "center") {
        profilPosition = anzahlNormaleProfile / 2 + 1;
      }
    }

    // for damit in keinem Fall Endlosschleife
    let decorprofilAlreadyInserted = false;
    for (let i = 0; i < 100; i++) {
      const anzahlTeile = getAnzahlTeile() + (leiste.unten && !isSenkrechtprofile ? 0 : 1);
      const matchesPosition = profilPosition == anzahlTeile;
      const useDekorprofil = dekorprofil && matchesPosition;
      const matchingProfil = useDekorprofil ? dekorprofil : profil;
      const addTeilImmerStücklistenRelevant = useDekorprofil
        ? (...args: any[]) => {
            decorprofilAlreadyInserted = true;
            const modell = addTeil(...args);
            this.modelleImmerStücklistenRelevant.push(modell);
          }
        : (...args: any[]) => {
            //hacky, but enforces decorprofilAlreadyInserted to be and the right position argument List
            args[6] = decorprofilAlreadyInserted;
            addTeil(...args);
          };
      const getAdditionalCuttingOptions = () => {
        const currentCount = getAnzahlTeile() + (leiste.unten ? 0 : 1);
        if (!isAnschluss || currentCount <= gesamtanzahlZugeschnittenerProfile) {
          return {};
        }
        const aktuelleAnzahlZugeschnittenerProfile =
          currentCount - gesamtanzahlZugeschnittenerProfile - 1;
        const offset = aktuelleAnzahlZugeschnittenerProfile * profilabstand(profil);
        const höheUnterkante = profilHöhe * aktuelleAnzahlZugeschnittenerProfile + offset;
        const höheOberkante = profilHöhe * (1 + aktuelleAnzahlZugeschnittenerProfile) + offset;
        const gesamtHöheZuschnitt =
          gesamtanzahlZugeschnittenerProfile * profilHöhe +
          (gesamtanzahlZugeschnittenerProfile - 1) * profilabstand(profil);
        return {
          diagonalCutted: true,
          diagonalCuttingprops: {
            left: anschlussLeft,
            bottom: sizeX * (1 - höheUnterkante / gesamtHöheZuschnitt),
            top: sizeX * (1 - höheOberkante / gesamtHöheZuschnitt),
          },
        };
      };

      const istUnterstesProfil = i == 0;
      const cutUnterstesProfil = wegschneidenHöheUnterstesProfilOffsetY > epsilon;
      const istOberstesProfil =
        getY() + profilabstand(profil) + modelHeight(matchingProfil) + epsilon > sizeY;

      if (
        leiste.dekorprofilleisteUnten &&
        dekorprofil &&
        matchesPosition &&
        passtTeil(leiste.dekorprofilleisteUnten)
      ) {
        addTeilImmerStücklistenRelevant(leiste.dekorprofilleisteUnten);
      }

      if ((hasRahmen || isSenkrechtprofile) && istUnterstesProfil && cutUnterstesProfil) {
        if (!passtTeil(matchingProfil)) {
          break;
        }
        const heightUnterstesProfil = Math.max(
          0,
          modelHeight(matchingProfil) - wegschneidenHöheUnterstesProfilOffsetY
        );
        addTeilImmerStücklistenRelevant(
          matchingProfil,
          heightUnterstesProfil,
          cutBottom,
          undefined,
          undefined,
          undefined,
          useDekorprofil
        );
      } else if ((hasRahmen || isSenkrechtprofile) && istOberstesProfil) {
        if (!isSenkrechtprofile && !this.element.isRhombusOderRhombusHolz && leiste.zwischen) {
          if (!passtTeil(leiste.zwischen)) {
            break;
          }
          addTeil(leiste.zwischen, leiste.zwischen?.modelSize.y, cutNowhere, true);
        }

        const height = Math.min(
          modelHeight(matchingProfil),
          sizeY - (getY() + profilabstand(matchingProfil))
        );
        if (height > 0.01) {
          addTeilImmerStücklistenRelevant(
            matchingProfil,
            height,
            cutTop,
            undefined,
            getAdditionalCuttingOptions(),
            useDekorprofil
          );
        }
        break;
      } else {
        if (
          !isSenkrechtprofile &&
          !this.element.isRhombusOderRhombusHolz &&
          !istUnterstesProfil &&
          leiste.zwischen
        ) {
          if (!passtTeil(leiste.zwischen)) {
            break;
          }
          addTeil(leiste.zwischen, leiste.zwischen?.modelSize.y, cutNowhere, true);
        }

        if (!passtTeil(matchingProfil)) {
          break;
        }
        addTeilImmerStücklistenRelevant(
          matchingProfil,
          modelHeight(matchingProfil),
          undefined,
          undefined,
          getAdditionalCuttingOptions(),
          useDekorprofil
        );
      }

      if (
        leiste.dekorprofilleisteOben &&
        dekorprofil &&
        matchesPosition &&
        passtTeil(leiste.dekorprofilleisteOben)
      ) {
        addTeilImmerStücklistenRelevant(leiste.dekorprofilleisteOben);
      }
    }
    const gesamtHöheZuschnitt =
      gesamtanzahlZugeschnittenerProfile * profilHöhe +
      (gesamtanzahlZugeschnittenerProfile - 1) * profilabstand(profil);
    !isSenkrechtprofile &&
      maybeAddLeisteOben(
        isAnschluss ? (anschlussLeft ? 1 : -1) * Math.atan(sizeX / gesamtHöheZuschnitt) : undefined
      );
    this.physicalHeight = getY();
  }

  updateSystemSenkrechtprofile(): void {
    this.drehbaresObjekt.reset();
    this.updateProfile(true);
    const { unten, oben } = this.element.getLeisten();
    [
      { leiste: unten, positionY: 0 },
      { leiste: oben, positionY: this.size.y },
    ].forEach(({ leiste, positionY }) => {
      const isOben = positionY > 0;
      if (leiste) {
        const model = this.addModel(
          leiste.meta,
          v3(this.size.x, Infinity, Infinity),
          v3(0, 0, 0),
          {},
          isOben ? this.drehbaresObjekt : this
        );
        const profilabstand = leiste.modelMaterialRelation.Model.Profilabstand;
        model.position.y = positionY + profilabstand;
        isOben && this.drehbaresObjekt.rotateAround(model.position, Maths3.vector.x, Math.PI);
      }
    });
  }

  teilBuilderY(isSenkrechtprofile = false): any {
    const sizeX = isSenkrechtprofile ? this.size.y : this.size.x;
    const sizeY = isSenkrechtprofile
      ? this.size.x
      : this.size.y + this.element.verringerterBodenabstand;
    const leiste = this.element.getLeisten();
    const profil = this.element.teilInfo(this.meta);
    const dekorprofil = this.element.showProfil
      ? this.element.teilInfo(this.element.profilMeta)
      : null;
    const { profilAnzahl, maxPosition } = this.element.sonderprofilMinMax;
    const hatProfilleiste =
      this.element.showProfil &&
      this.element.profilMeta?.Profilkategorie?.some((k) => !Commons.isEmpty(k.Profilleiste));
    const hasRahmen = this.element.rahmenMeta != null;
    let y = 0;
    let anzahlTeile = 0;
    let shuffleMaterialIndex = this.shuffleMaterialIndexOffset;
    const modelHeight = (teil) => teil?.modelSize.y;
    const profilabstand = (teil) => {
      // 0.01 Profilabstand damit bei Rhombus keine Lücke zwischen
      // der oberen Adapterleiste und dem darüber liegenden Profil bleibt
      const isProfilÜberAdapterleiste =
        this.element.isRhombusOderRhombusHolz && hatProfilleiste && maxPosition + 2 == anzahlTeile;
      return anzahlTeile == 0
        ? 0
        : isProfilÜberAdapterleiste
          ? -0.01
          : teil.modelMaterialRelation.Model.Profilabstand;
    };

    const passtTeil = (teil, height = modelHeight(teil)) =>
      // die this.size.y ist eventuell nicht perfekt, daher * 0.78, damit eher ein Profil zuviel als zuwenig hinzugefügt wird, wenn
      // this.size.y nicht erreicht wird
      y + profilabstand(teil) + height * 0.78 < sizeY + epsilon;

    const addTeil = (
      teil: any,
      height = modelHeight(teil),
      cutYAt: symbol | undefined = cutNowhere,
      isZwischenleiste = false,
      additionalCuttingOptions: any = {},
      isDekorprofil = false,
      decorProfilAlreadyInserted = false,
      cutSizeXOverride: number | undefined = undefined,
      positionXOffset = 0
    ): ModelGhost => {
      let model: ModelGhost | null = null;
      const rotateProfile =
        teil == profil &&
        decorProfilAlreadyInserted &&
        isSenkrechtprofile &&
        leiste.dekorprofilleisteOben;
      const anzahl = isDekorprofil ? profilAnzahl : 1;
      const { lichteWeite, gapLinks, gapRechts } = this.element;
      for (let i = 0; i < anzahl; i++) {
        y += profilabstand(teil);
        let cutSizeX =
          teil.modelMaterialRelation.Model.ZuschnittLichteWeite && !hasRahmen
            ? lichteWeite - gapLinks - gapRechts
            : sizeX;

        if (hasRahmen && teil.meta.Objekttyp == "Aufsatzleiste") {
          cutSizeX += this.element.getRahmenInnenabstand() * 2;
        }

        if (isSenkrechtprofile && teil.meta.Objekttyp == "Profilleiste") cutSizeX = sizeX;
        if (isSenkrechtprofile && leiste.oben) {
          cutSizeX -= (leiste.oben.modelSize.y + leiste.unten.modelSize.y) / 2 + 0.002;
        }
        if (cutSizeXOverride !== undefined) {
          cutSizeX = cutSizeXOverride;
        }

        const cutSizeY = cutYAt == cutNowhere ? Infinity : height;
        model = this.addModel(
          teil.meta,
          v3(cutSizeX, cutSizeY, Infinity),
          v3(0, cutYAt == cutBottom || rotateProfile ? 1 : 0, 0),
          additionalCuttingOptions
        );
        model.shuffleMaterialIndex = shuffleMaterialIndex++;

        // fixme wenn cutBottom dann sollte eigentlich das Objekt neu positioniert werden,
        // so dass die alte Model - GridPos - Bedingung erhalten bleibt. Allerdings ist das
        // nicht trivial.. also besser, einfach das Objekt um den berechneten Delta-Betrag verschieben
        model.position.y = y + (cutYAt == cutBottom ? height - modelHeight(teil) : 0);
        model.position.x = (isSenkrechtprofile && !this.element.isTor ? 0.02 : 0) + positionXOffset;
        if (rotateProfile) {
          model.quaternion.setFromAxisAngle(v3(0, 0, 1), Math.PI);
          model.position.y += modelHeight(teil);
          model.position.x += model.getSize().x;
        } else {
          model.quaternion.setFromAxisAngle(v3(0, 0, 1), 0);
        }
        if (hasRahmen && teil.meta.Objekttyp == "Aufsatzleiste") {
          model.position.x -= this.element.getRahmenInnenabstand();
          model.position.y += this.element.getRahmenInnenabstand();
        }

        y += height;
        !isZwischenleiste && anzahlTeile++;
      }
      return model;
    };

    const getY = () => y;

    const getAnzahlTeile = () => anzahlTeile;

    const maybeAddLeisteUnten = () => {
      if (leiste.unten && !hasRahmen) {
        const m = addTeil(leiste.unten);
        this.modelleImmerStücklistenRelevant.push(m);
      }
    };

    const maybeAddLeisteOben = (angle = 0): void => {
      let m: ModelGhost | null = null;
      if (leiste.aufsatzleiste) {
        m = addTeil(leiste.aufsatzleiste, undefined, undefined, undefined, {
          rotateBeforeCutting: angle,
        });
      } else if (leiste.uAbschlussprofil && !this.element.isStandardHöhe) {
        m = addTeil(leiste.uAbschlussprofil, undefined, undefined, undefined, {
          rotateBeforeCutting: angle,
        });
      } else if (
        leiste.oben &&
        !hasRahmen &&
        sollAbschlussleisteObenErscheinen(this.element.meta, this.element.size.y)
      ) {
        m = addTeil(leiste.oben, undefined, undefined, undefined, { rotateBeforeCutting: angle });
      }
      if (m) {
        m.position.y += this.getAufsatzleisteOffset();
        this.modelleImmerStücklistenRelevant.push(m);
      }
    };

    return {
      maybeAddLeisteUnten,
      maybeAddLeisteOben,
      addTeil,
      leiste,
      passtTeil,
      profil,
      dekorprofil,
      getY,
      getAnzahlTeile,
      hasRahmen,
      modelHeight,
      profilabstand,
    };
  }

  updateVersetzteSenkrechtprofileVariabel(): void {
    const meta = this.meta!;
    const mmr = getMMR(meta);
    const modelSize = APP.modelsHelper.getBoundingBox(mmr).getSize(v3());
    const size = this.size;
    const gesamtBreite = size.x;
    const lattenBreite = modelSize.x;
    let lückenBreite = 0.04;
    let stepFull = lattenBreite + lückenBreite;
    const anzahlProfile = Math.floor(gesamtBreite / stepFull) + 1;
    lückenBreite = (gesamtBreite - anzahlProfile * lattenBreite) / (anzahlProfile - 1);
    stepFull = lattenBreite + lückenBreite;
    const start = 0;
    [1, -1].forEach((zDir) => {
      this.updateSenkrechtprofile({
        meta,
        start,
        anzahlProfile: anzahlProfile + (zDir === -1 ? 1 : 0),
        profilAbstand: lückenBreite,
        lattenBreite,
        zPosition: 0.02 * zDir,
      });
    });
    const { aufsatzleiste } = this.element.getLeisten();
    if (aufsatzleiste) {
      const model = this.addModel(
        aufsatzleiste.meta,
        v3(this.size.x, Infinity, Infinity),
        v3(0, 0, 0)
      );
      const profilabstand = aufsatzleiste.modelMaterialRelation.Model.Profilabstand;
      model.position.y = this.size.y + profilabstand;
    }
  }

  updateSenkrechtprofileVariabel(): void {
    const meta = this.meta;
    const mmr = getMMR(meta);
    const modelSize = APP.modelsHelper.getBoundingBox(mmr).getSize(v3());
    const rahmenInnenBox = this.flügel.rahmenInnenBox.clone();
    const size = rahmenInnenBox.getSize(v3());
    const gesamtBreite = size.x;
    const lattenBreite = modelSize.x;
    const minLueckenBreite = 0.04;
    const anzahlProfile = Math.floor((gesamtBreite - minLueckenBreite) / lattenBreite);
    const profilAbstand = (gesamtBreite - lattenBreite) / (anzahlProfile - 1) - lattenBreite;
    const start = 0;
    this.updateSenkrechtprofile({
      meta,
      start,
      anzahlProfile,
      profilAbstand,
      lattenBreite,
    });
  }

  updateSenkrechtprofileFix(): void {
    const detailSerieKey = this.element.meta.DetailSerie?.[0]?.Key ?? "";
    const isRajaWPC = detailSerieKey === "RAJA WPC ALU XL";
    const isRajaAluXL = detailSerieKey.includes("RAJA ALU XL") && !isRajaWPC;
    const isRajaSerie = isRajaWPC || isRajaAluXL;
    const meta = this.meta;
    const mmr = getMMR(meta);
    const modelSize = APP.modelsHelper.getBoundingBox(mmr).getSize(v3());
    const rahmenInnenBox = this.flügel.rahmenInnenBox.clone();
    const size = rahmenInnenBox.getSize(v3());
    const gesamtBreite = size.x + (isRajaWPC ? rahmenInnenBox.min.x * 2 : 0);
    const lattenBreite = modelSize.x;
    const modelMeta = mmr.Model;
    const profilAbstand = modelMeta.Profilabstand ?? 0;
    const stepFull = lattenBreite + profilAbstand;
    const anzahlProfile = Math.max(
      1,
      Math.floor((gesamtBreite + (isRajaWPC ? lattenBreite : profilAbstand)) / stepFull)
    );
    const cutX = (((gesamtBreite + lattenBreite) % stepFull) + profilAbstand) / 2;
    // Kann von dem vorgegebenen Profilabstand abweichen, wichtiger ist hier ein optisch einheitlicher Abstand
    const abstandPhysical = isRajaSerie
      ? profilAbstand
      : (gesamtBreite - anzahlProfile * lattenBreite) / (anzahlProfile + 1);
    let start: number;
    if (isRajaAluXL) {
      // Latten zentrieren: Gesamtbelegung berechnen, Rest gleichmäßig links und rechts verteilen
      const totalUsed = anzahlProfile * lattenBreite + (anzahlProfile - 1) * profilAbstand;
      const remaining = gesamtBreite - totalUsed;
      start = remaining / 2;
    } else if (isRajaWPC) {
      start = 0;
    } else {
      start = abstandPhysical + rahmenInnenBox.min.x;
    }
    this.updateSenkrechtprofile({
      meta,
      start,
      anzahlProfile,
      profilAbstand: abstandPhysical,
      lattenBreite,
      zPosition: 0,
      isRajaSerie: isRajaWPC,
      cutX,
    });
  }

  updateSenkrechtprofile({
    meta,
    start,
    anzahlProfile,
    profilAbstand,
    lattenBreite,
    zPosition = 0,
    isRajaSerie = false,
    cutX,
  }: {
    meta: any;
    start: number;
    anzahlProfile: number;
    profilAbstand: number;
    lattenBreite: number;
    zPosition?: number;
    isRajaSerie?: boolean;
    cutX?: number;
  }): void {
    const isBackside = zPosition < 0;
    const stepFull = profilAbstand + lattenBreite;
    let x = start;
    let shuffleMaterialIndex = this.shuffleMaterialIndexOffset;
    for (let i = 0; i < anzahlProfile; i++) {
      const isFirst = i == 0;
      const isLast = i == anzahlProfile - 1;
      const isSecondToLast = i == anzahlProfile - 2;
      let sizeX = lattenBreite;
      if (isFirst || isLast) {
        if (isBackside) {
          sizeX *= 0.5;
        } else if (isRajaSerie) {
          sizeX = Math.max(0.01, cutX);
        }
      }
      const drehbaresObjekt = new PivotKette(this.pivotPoint);
      const model = this.addModel(
        meta,
        v3(sizeX, this.size.y, Infinity),
        v3(0, 0, 0),
        {},
        drehbaresObjekt
      );
      model.shuffleMaterialIndex = shuffleMaterialIndex++;
      const temp = x + (isBackside ? lattenBreite : 0);
      model.position.x = temp;
      model.position.z = zPosition;
      let step = stepFull;
      if (isBackside) {
        drehbaresObjekt.rotateAround(model.position, Maths3.vector.y, Math.PI);
        if (isFirst) {
          step = stepFull / 2;
          model.position.x += lattenBreite / 2;
        } else if (isSecondToLast) {
          step = profilAbstand / 2;
        } else if (isLast) {
          drehbaresObjekt.rotateAround(model.position, Maths3.vector.x, Math.PI);
          model.position.y = this.size.y;
        }
      } else if (isRajaSerie) {
        step = isFirst ? Math.max(0.01, cutX) + profilAbstand : stepFull;
      }
      x += step;
      drehbaresObjekt.update3d();
      this.senkrechtprofilDrehachsen.push(drehbaresObjekt);
    }
    this.physicalHeight = this.size.y;
  }

  /**
   * @param iterator {StücklistenIterator}
   * @param ObjectGroupTag {string}
   */
  collectArtikel(
    iterator: (params: any) => void,
    ObjectGroupTag: string | undefined = undefined,
    JacoId: number | undefined = undefined
  ): void {
    let modelle = !(this.element.isTor || this.element.isAnschluss)
      ? this.modelle
      : this.modelleImmerStücklistenRelevant;

    // Dekore und Adapter sollen keine Stücklistenposition bekommen
    // wenn sich die beiden in einem Sondertor befinden
    if (this.element.isOhneStücklistenpositionDekorprofil) {
      modelle = modelle.filter(
        (m: ModelGhost) =>
          !["Profilleiste", "Füllung"].includes((m as any).stücklistenMeta.Objekttyp)
      );
    }

    modelle
      .filter((model) => (model as any).stücklistenMeta)
      .forEach((model) => {
        const zuschnittsoptimiert = (model as any).stücklistenMeta.Zuschnittsoptimiert?.[0];
        const Breite = model.getBox().getSize(v3()).x;
        const meta =
          !zuschnittsoptimiert?.Zuschnitt &&
          (isEmpty(zuschnittsoptimiert?.Standardbreite) ||
            Maths.round(Breite, 2) < Math.max(...zuschnittsoptimiert.Standardbreite)) &&
          zuschnittsoptimiert?.MaßFüllung
            ? zuschnittsoptimiert?.MaßFüllung
            : (model as any).stücklistenMeta;
        const params = { ObjektMeta: meta, ObjectGroupTag, Breite, JacoId };
        iterator(params);
      });
    if (this.element.isRhombusNichtHolz) {
      const anzahlProfile = Commons.count(
        modelle,
        (model) => (model as any).stücklistenMeta?.Objekttyp == "Füllung"
      );
      const profilträger = APP.richMetadata.findEnsureAnyObjekt("2649 SYSTEM Rhombus Profilträger");
      const anzahlProfilträger = Math.ceil((anzahlProfile * 2) / 5);
      for (let i = 0; i < anzahlProfilträger; i++) {
        iterator({ ObjektMeta: profilträger, ObjectGroupTag, JacoId });
      }
    }
  }

  get boxPhysical(): Box3 {
    return this.modelle.reduce((acc, m) => acc.union(m.getBox()), new Box3());
  }

  get heightPhysical(): number {
    return _.max(this.modelle.map(heightBoxWorldPhysical))!;
  }

  get hasLichtleiste(): boolean {
    return this.modelle.some((m) => m.model.modelMaterialRelation.Model.Key.match(/licht/i));
  }

  createJaco3d() {
    return new Füllung3d(this);
  }

  /**
   * Zeigt das Lupensymbol auf beiden Seiten der Füllung (als transparentes Sprite rechts unten)
   * @returns {Promise<void>}
   */
  async updateDetailBild(): Promise<void> {
    const füllungJaco = this;
    const elementJaco = this.element;
    if (!elementJaco) return;

    Scenes.remove(this.detailBildGroup);
    this.detailBildGroup = null;
    if (!APP.isTorkonfigurator && !APP.isSaving && !this.element.isAnschlussMitABLupe) return;

    if (!füllungJaco) return;
    const darstellung = APP.richMetadata.findDarstellung2D(füllungJaco.meta.Key);
    if (!darstellung) return;
    const detailBildFront = elementJaco.seiteGewechselt
      ? darstellung.DetailBildB
      : darstellung.DetailBildA;
    const detailBildBack = elementJaco.seiteGewechselt
      ? darstellung.DetailBildA
      : darstellung.DetailBildB;

    const detailBildGroup = (this.detailBildGroup = new Group());
    this.jaco3d?.add(detailBildGroup);

    async function addBild(
      jaco: Füllung,
      bildUrl: string | undefined,
      isFront: boolean
    ): Promise<void> {
      if (bildUrl) {
        const icon = Textures.createImagePlane(await Images.loadImage(bildUrl), {
          pixelPerUnit: 800,
          side: FrontSide,
        });
        icon.renderOrder = 10000;
        if (!isFront) icon.quaternion.copy(Maths3.quaternion.y180);
        detailBildGroup.add(icon);
        const iconHalfSize = new Vector3();
        icon.posbox.box
          .applyQuaternion(elementJaco.parent.getWorldQuaternion())
          .getSize(iconHalfSize)
          .multiplyScalar(0.5);

        // Etwas komplizierter Code um zu schauen ob das detailBild links oder rechts unten sein soll
        // Bei Schiebetoren soll die Lupe neben dem Motor positioniert werden
        // Bei anderen Toren soll sie neben den Kloben positioniert werden
        let detailBildLinks: boolean;
        if (elementJaco.isSchiebetor) {
          const { schiebetormotorseiteAuswahlwert, isAnschlagLinks, isSchiebetorMotorseiteA } =
            elementJaco;
          const motorseiteA = schiebetormotorseiteAuswahlwert?.Key == "Schiebetormotorseite_A";
          const isMotorseite = isSchiebetorMotorseiteA != isFront;
          detailBildLinks = !isFront;
          if (motorseiteA != isAnschlagLinks) {
            detailBildLinks = !detailBildLinks;
          }
          if (isMotorseite) {
            icon.name = "DetailBild";
          }
        } else {
          const öffnungNachInnen = elementJaco.isÖffnungNachInnen;
          const isKlobenSeite = öffnungNachInnen != isFront;
          const anschlagLinks = elementJaco.isAnschlagLinks;
          detailBildLinks = !isFront;
          if (isKlobenSeite && !anschlagLinks) {
            detailBildLinks = !detailBildLinks;
          }
          if (isKlobenSeite) {
            icon.name = "DetailBild";
          }
        }
        const versatz = v3(
          detailBildLinks ? -iconHalfSize.x : iconHalfSize.x,
          iconHalfSize.y,
          isFront ? 0.03 : -0.03
        );
        icon.position.copy(
          jaco
            .getBox()
            .grid(detailBildLinks ? 1 : 0, 0, 0.5)
            .add(versatz)
        );
      }
    }

    await addBild(füllungJaco, detailBildFront, true);
    await addBild(füllungJaco, detailBildBack, false);
  }
}
